import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Button, Markdown, Spinner, Text } from "grommet";
import { Close, Trash } from "grommet-icons";
import { BRAND_GREEN, themed } from "./chatTheme";

// LLM-provided URLs are untrusted; only allow http(s) so javascript: links can't run
const safeUrl = (url) => (typeof url === "string" && /^https?:\/\//i.test(url) ? url : undefined);

// source shape isn't fixed by the API contract, so accept plain URLs or objects
const toSourceCard = (source) => {
    if (typeof source === "string") {
        return { ...splitFileName(source), url: safeUrl(source), pages: [] };
    }
    const url = safeUrl(source.url || source.link);
    const pages = Array.isArray(source.pages) && source.pages.length > 0
        ? source.pages
        : source.page != null ? [source.page] : [];
    return { ...splitFileName(source.title || source.name), url, pages };
};

const splitFileName = (name) => {
    const match = /^(.*)\.([a-z0-9]{2,5})$/i.exec(name || "");
    return match ? { title: match[1], extension: match[2].toUpperCase() } : { title: name || "Untitled", extension: null };
};

const hostnameOf = (url) => {
    try {
        return new URL(url).hostname;
    } catch {
        return null;
    }
};

// raw HTML in LLM output is untrusted, so only markdown syntax is rendered
const markdownOptions = { disableParsingRawHTML: true };
const markdownComponents = {
    p: { component: "p" },
    a: { component: "a", props: { target: "_blank", rel: "noopener noreferrer" } },
};

const slideUp = keyframes`
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: ${themed("rgba(0, 0, 0, 0.25)", "rgba(0, 0, 0, 0.45)")};
    animation: ${fadeIn} 0.2s ease-out;
`;

const Sheet = styled.div`
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 0);
    z-index: 1001;
    display: flex;
    flex-direction: column;
    width: min(960px, calc(100vw - 32px));
    height: min(720px, 80vh);
    box-sizing: border-box;
    padding: 16px 24px 24px;
    border-radius: 24px 24px 0 0;
    border: 1px solid ${themed("rgba(0, 0, 0, 0.08)", "rgba(255, 255, 255, 0.15)")};
    border-bottom: none;
    background: ${themed("rgba(255, 255, 255, 0.94)", "rgba(28, 28, 28, 0.88)")};
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: ${themed("0 -8px 32px rgba(0, 0, 0, 0.15)", "0 -8px 32px rgba(0, 0, 0, 0.4)")};
    animation: ${slideUp} 0.3s ease-out;
`;

const Messages = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px 0 16px;
`;

// user turns are a subtle right-aligned bubble; assistant replies render as plain full-width text
const Bubble = styled.div`
    flex: 0 0 auto;
    align-self: ${(props) => (props.$fromUser ? "flex-end" : "stretch")};
    max-width: ${(props) => (props.$fromUser ? "70%" : "100%")};
    padding: ${(props) => (props.$fromUser ? "8px 16px" : "0")};
    border-radius: 8px;
    background: ${(props) => (props.$fromUser ? themed("#F2F2F2", "#2A2D38")(props) : "transparent")};
    color: ${(props) => (props.$isError ? themed("#CC1F1A", "#FC6161")(props) : themed("#1c1c1c", "#E6E8E9")(props))};
    font-size: 15px;
    line-height: 1.5;
    overflow-wrap: anywhere;
`;

const PlainText = styled.div`
    white-space: pre-wrap;
`;

const MarkdownBody = styled.div`
    p {
        margin: 0 0 10px;
    }

    ul,
    ol {
        margin: 0 0 10px;
        padding-left: 20px;
    }

    li {
        margin-bottom: 4px;
    }

    > div > *:last-child {
        margin-bottom: 0;
    }

    a {
        color: ${themed("#017D63", "#7fcfb8")};
    }
`;

const Sources = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${themed("rgba(0, 0, 0, 0.08)", "rgba(255, 255, 255, 0.12)")};
`;

const SourceCard = styled.a`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid ${themed("rgba(0, 0, 0, 0.1)", "rgba(255, 255, 255, 0.15)")};
    background: ${themed("#ffffff", "rgba(255, 255, 255, 0.05)")};
    color: ${themed("#1c1c1c", "#ffffff")};
    text-decoration: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    &[href]:hover,
    &[href]:focus-visible {
        border-color: ${BRAND_GREEN};
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        transform: translateY(-1px);
        outline: none;
    }

    &:not([href]) {
        cursor: default;
        opacity: 0.7;
    }
`;

const SourceTitle = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
`;

const SourceMeta = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${themed("rgba(0, 0, 0, 0.6)", "rgba(255, 255, 255, 0.65)")};
`;

const FileBadge = styled.span`
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: ${BRAND_GREEN};
`;

const SourceLinkText = styled.span`
    margin-top: auto;
    font-size: 12px;
    color: ${themed("#017D63", "#7fcfb8")};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const hasSourceContent = (source) => {
    if (!source) return false;
    if (typeof source === "string") return source.trim() !== "";
    return Boolean(source.title || source.name || source.url || source.link);
};

const SourceCards = ({ sources }) => {
    const visible = Array.isArray(sources) ? sources.filter(hasSourceContent) : [];
    if (visible.length === 0) return null;
    return (
        <Sources>
            {visible.map((source, index) => {
                const { title, extension, url, pages } = toSourceCard(source);
                const host = url && hostnameOf(url);
                return (
                    <SourceCard
                        key={index}
                        href={url}
                        target={url ? "_blank" : undefined}
                        rel={url ? "noopener noreferrer" : undefined}
                        title={title}
                    >
                        <SourceTitle>{title}</SourceTitle>
                        <SourceMeta>
                            {extension && <FileBadge>{extension}</FileBadge>}
                            {pages.length > 0 && <span>{pages.length === 1 ? "Page" : "Pages"} {pages.join(", ")}</span>}
                        </SourceMeta>
                        <SourceLinkText>{url ? `Open on ${host || "source"} ↗` : "Link unavailable"}</SourceLinkText>
                    </SourceCard>
                );
            })}
        </Sources>
    );
};

export const ChatPanel = ({ messages, pending, onClose, onClearChat, inputBar }) => {
    const bottomRef = useRef(null);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, pending]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    return (
        <>
            <Backdrop onClick={onClose} />
            <Sheet role="dialog" aria-label="HPE AI chat">
                <Box direction="row" align="center" justify="between" flex={false} margin={{ bottom: "small" }}>
                    <Text color="text-strong" weight="bold" size="large">HPE AI</Text>
                    <Box direction="row" align="center" gap="medium">
                        <Button
                            plain
                            icon={<Trash color="text" size="small" />}
                            label={<Text color="text" size="small">Clear chat</Text>}
                            onClick={onClearChat}
                            disabled={pending}
                            a11yTitle="Clear chat history"
                        />
                        <Button plain icon={<Close color="text" />} onClick={onClose} a11yTitle="Close chat" />
                    </Box>
                </Box>
                <Messages>
                    {messages.length === 0 && (
                        <Text color="text-weak" alignSelf="center" margin={{ top: "large" }}>
                            Ask anything about competitors, products, or resources.
                        </Text>
                    )}
                    {messages.map((m, i) => {
                        const isBotAnswer = m.from === "bot" && !m.isError;
                        return (
                            <Bubble key={i} $fromUser={m.from === "user"} $isError={m.isError}>
                                {isBotAnswer ? (
                                    <MarkdownBody>
                                        <Markdown options={markdownOptions} components={markdownComponents}>
                                            {m.text || ""}
                                        </Markdown>
                                    </MarkdownBody>
                                ) : (
                                    <PlainText>{m.text}</PlainText>
                                )}
                                <SourceCards sources={m.sources} />
                            </Bubble>
                        );
                    })}
                    {pending && <Box flex={false} alignSelf="start" pad="xsmall"><Spinner size="small" /></Box>}
                    <div ref={bottomRef} />
                </Messages>
                {inputBar}
            </Sheet>
        </>
    );
};
